// 即時の配信（WebSocket）。サーバーから「何が変わったか」だけが届き、画面はそれを受けて読み直す。
// 切れたらつなぎ直す。つながらない間は各画面の定期的な読み直し（1 分）で補う
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from './api';

export type Topic = 'status' | 'deliveries' | 'rollcalls' | 'alerts' | 'issues' | 'messages' | 'locks' | 'settings';
type Listener = (topics: Topic[]) => void;
type Ctx = { subscribe: (f: Listener) => () => void; connected: boolean };
const RtCtx = createContext<Ctx>({ subscribe: () => () => {}, connected: false });

/** 定期的な読み直しの間隔（つながっているときは長め） */
export const POLL_MS = 60_000;

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const listeners = useRef(new Set<Listener>());
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let ws: WebSocket | null = null;
    let stop = false;
    let wait = 1000;
    let ping: ReturnType<typeof setInterval> | undefined;
    const open = async () => {
      if (stop) return;
      try {
        const { ticket } = await api.post<{ ticket: string }>('/v1/auth/ws-ticket', {});
        ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/v1/ws/office?ticket=${encodeURIComponent(ticket)}`);
        ws.onopen = () => { setConnected(true); wait = 1000; ping = setInterval(() => ws?.readyState === WebSocket.OPEN && ws.send('ping'), 30_000); };
        ws.onmessage = (e) => {
          if (typeof e.data !== 'string' || e.data === 'pong') return;
          try { const m = JSON.parse(e.data) as { type: string; topics?: Topic[] }; if (m.type === 'changed' && m.topics) listeners.current.forEach((f) => f(m.topics!)); } catch { /* 知らない形は無視 */ }
        };
        ws.onclose = () => { setConnected(false); clearInterval(ping); if (!stop) setTimeout(open, wait = Math.min(wait * 2, 30_000)); };
      } catch { if (!stop) setTimeout(open, wait = Math.min(wait * 2, 30_000)); }
    };
    void open();
    return () => { stop = true; clearInterval(ping); ws?.close(); };
  }, []);
  const subscribe = (f: Listener) => { listeners.current.add(f); return () => { listeners.current.delete(f); }; };
  return <RtCtx.Provider value={{ subscribe, connected }}>{children}</RtCtx.Provider>;
}

export const useRealtimeConnected = () => useContext(RtCtx).connected;

/** 「〇分前」の表示を進めるため、ms ごとに描き直す */
export function useTicker(ms = 15_000) {
  const [, set] = useState(0);
  useEffect(() => { const t = setInterval(() => set((x) => x + 1), ms); return () => clearInterval(t); }, [ms]);
}

/**
 * topics のどれかが変わったら reload を呼ぶ。続けて届いたときはまとめる（0.5 秒）。
 * つながっていないときのために、POLL_MS ごとにも呼ぶ
 */
export function useLive(topics: Topic[], reload: () => void) {
  const { subscribe } = useContext(RtCtx);
  const fn = useRef(reload);
  fn.current = reload;
  const key = topics.join(',');
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const off = subscribe((changed) => {
      if (!changed.some((x) => key.split(',').includes(x))) return;
      clearTimeout(t); t = setTimeout(() => fn.current(), 500);
    });
    const poll = setInterval(() => fn.current(), POLL_MS);
    return () => { off(); clearTimeout(t); clearInterval(poll); };
  }, [key, subscribe]);
}
